import { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type { PreviewCandidateAppointment } from '@eluve/api-contract';

interface UsePviCandidateAppointmentsResult {
  candidates: PreviewCandidateAppointment[] | null;
  isLoading: boolean;
}

/**
 * Loads up to 3 candidate appointments to use as PVI preview context. Returns
 * `null` for `candidates` while the request is in flight; an empty array when
 * the user has nothing eligible (chooser falls back to sample-only).
 */
export const usePviCandidateAppointments = (
  tenantId: string,
): UsePviCandidateAppointmentsResult => {
  const apiClient = useApiClient();
  const [candidates, setCandidates] = useState<
    PreviewCandidateAppointment[] | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    // Send the caller's IANA timezone so the API evaluates "today" in
    // the user's local zone instead of UTC.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    void apiClient.insightsPreview
      .listCandidates({ params: { tenantId }, query: { timezone } })
      .then((response) => {
        if (cancelled) return;
        if (response.status === 200) {
          setCandidates(response.body.candidates);
        } else {
          setCandidates([]);
        }
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId]);

  return { candidates, isLoading: candidates === null };
};
